import React, { useState, useEffect } from 'react';
import { MdCheckBox, MdOutlineFilePresent, MdFileDownload, MdArrowBack, MdArrowForward } from 'react-icons/md';
import { useFormContext } from '../../../context/FormContext';
import { useReadOnly } from '../../../hooks/useReadOnly';
import { SignupLayout } from '../../ui/SignupLayout';
import { OptionCard } from '../../ui/OptionCard';
import Session from '../../auth/Session';

export function PaymentMethod({ history }) {
    const { formData, setField, setStep } = useFormContext();
    const { isReadOnly } = useReadOnly();
    const [error, setError] = useState('');
    const [downloading, setDownloading] = useState(false);

    useEffect(() => {
        setStep('payment');
    }, []);

    const handleContinue = () => {
        if (!formData.PaymentType) {
            setError('Please select a payment method.');
            return;
        }
        setError('');
        if (formData.PaymentType === 'ach') {
            history.push('/signup/payment/bank');
        } else {
            history.push('/signup/payment/card');
        }
    };

    const handleBack = () => history.push('/signup/sign');

    const handleSelectMethod = (type) => {
        setField('PaymentType', type);
        setError('');
    };

    const handleDownloadApplication = async () => {
        if (!formData.QuoteId) return;
        setDownloading(true);
        try {
            const token = Session.getToken();
            const response = await fetch(`api/Document/GetLatestApplication/${formData.QuoteId}`, {
                headers: token ? { Authorization: `Bearer ${token}` } : {},
            });

            if (!response.ok) {
                console.error('Could not load application.');
                return;
            }

            const blob = await response.blob();
            const url = URL.createObjectURL(blob);

            const a = document.createElement('a');
            a.href = url;
            a.download = 'Application.pdf';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            setTimeout(() => URL.revokeObjectURL(url), 10000);
        } catch (err) {
            console.error('Could not load application.', err);
        } finally {
            setDownloading(false);
        }
    };

    return (
        <SignupLayout showProgress={false}>

            {/* -- Application approved banner -- */}
            <div className="payment-approved">
                <div className="payment-approved__header">
                    <MdCheckBox size={20} className="payment-approved__icon" />
                    <strong>Application approved</strong>
                </div>
                <p className="payment-approved__desc">
                    Your application is complete and a copy has been sent to your email.
                </p>

                <div
                    // className="payment-approved__download"
                    className={`payment-approved__download ${isReadOnly ? 'payment-approved__download--disabled' : ''}`}
                    onClick={handleDownloadApplication}
                    style={{ cursor: downloading ? 'wait' : 'pointer' }}
                >
                    <MdOutlineFilePresent size={18} />
                    <span>{downloading ? 'Loading…' : 'Application.pdf'}</span>
                    <MdFileDownload size={18} className="payment-approved__download-icon" />
                </div>
            </div>

            {/* -- Heading -- */}
            <h1 className="text-h1" style={{ marginTop: 'var(--space-lg)' }}>
                Select a payment method
            </h1>
            <p className="text-default" style={{marginBottom: `var(--space-lg)`}}>
                Choose how you'd like to pay for your coverage. Your payment information is
                encrypted and secure.
            </p>

            {/* -- Option cards -- */}
            <div className="dp-options">
                <OptionCard
                    selected={formData.PaymentType === 'ach'}
                    onChange={() => handleSelectMethod('ach')}
                >
                    <div>
                        <p className="text-h4--primary" style={{ marginBottom: 'var(--space-2xs)' }}>
                            Bank Account (ACH)
                        </p>
                        <p className="text-small" style={{ marginBottom: 0 }}>
                            Connect your bank account for direct payment via ACH transfer.
                        </p>
                    </div>
                </OptionCard>

                <OptionCard
                    selected={formData.PaymentType === 'creditCard'}
                    onChange={() => handleSelectMethod('creditCard')}
                >
                    <div>
                        <p className="text-h4--primary" style={{ marginBottom: 'var(--space-2xs)' }}>
                            Credit or Debit Card
                        </p>
                        <p className="text-small" style={{ marginBottom: 0 }}>
                            Pay securely with Visa, Mastercard, American Express, or Discover.
                            A 3% processing fee will be applied.
                        </p>
                    </div>
                </OptionCard>
            </div>

            {error && (
                <span className="error-text field-error" style={{ display: 'block', marginTop: 'var(--space-xs)' }}>
                    {error}
                </span>
            )}

            {/* -- Navigation -- */}
            <div className="signup-nav">
                <button className="btn signup-cta btn--inverse-cta" onClick={handleBack}>
                    <MdArrowBack /> Back
                </button>
                <button className="btn signup-cta" onClick={handleContinue}>
                    Continue <MdArrowForward />
                </button>
            </div>
        </SignupLayout>
    );
}